import {
  decodeSilhouette,
  densestRowWindow,
  handTerritory,
  silhouetteRects,
  silhouetteRowOffset,
  type Silhouette,
  type SilhouetteBox,
  type SilhouetteRowWindow,
} from "@/lib/library/silhouette";
import {
  CELL_GAP,
  CELL_RADIUS,
  HAND_FILL,
  HAND_TERRITORY_FILL,
  PAINT_ORDER,
  RULE_COLOR,
  RULE_ROW_STEP,
  RULE_THICKNESS,
  SILHOUETTE_BACKGROUND,
  cellAlpha,
} from "@/lib/library/silhouette-style";

/**
 * Le rendu SVG de la silhouette d'un morceau. Tout le décodage et toute la
 * géométrie vivent dans `lib/library/silhouette.ts`, tous les réglages
 * esthétiques dans `lib/library/silhouette-style.ts` ; ce fichier ne fait que
 * peindre.
 *
 * Aucun hook : le composant reste rendable depuis un composant serveur comme
 * depuis un composant client. Le décodage coûte quelques milliers d'opérations
 * entières sur une chaîne de 496 caractères — mémoïser ça coûterait plus cher
 * en frontière client qu'il ne rapporterait.
 */

type Props = {
  /** Le champ `silhouette` du catalogue, tel quel. */
  silhouette: string | null;
  /** Largeur du repère de dessin. Donne aussi ses proportions au conteneur. */
  width: number;
  /** Hauteur du repère de dessin. */
  height: number;
  className?: string;
  /**
   * Décrit la figure à voix haute. Omis, la figure est décorative.
   *
   * Sur une carte, le lien porte déjà le titre et le compositeur : y ajouter
   * une description de la figure ferait lire un paragraphe par vignette, soit
   * vingt-quatre par page de catalogue. On ne la nomme que là où elle est le
   * sujet — sur la fiche.
   */
  label?: string;
  /**
   * Avancement dans l'aperçu audio, `0..1`. `null` hors lecture — la tête n'est
   * alors pas dessinée du tout, plutôt que posée au départ : une tête immobile
   * sur une figure muette se lit comme un lecteur en panne.
   */
  progress?: number | null;
  /**
   * Part du morceau que l'aperçu couvre, `0..1`.
   *
   * **C'est ce qui empêche la tête de mentir.** La figure décrit le morceau
   * entier sur toute sa largeur, alors que l'aperçu n'en joue que les vingt
   * premières secondes : sur les 221 pièces du catalogue, aucune ne dure moins
   * que ça, on entend 23 % du morceau en moyenne et 1,3 % dans le pire cas.
   * Faire balayer toute la largeur à la tête reviendrait à dessiner une donnée
   * fausse — exactement le défaut de la figure synthétique qu'on a remplacée.
   *
   * `1` (ou `null`) fait balayer toute la largeur, ce qui n'est juste que si
   * l'extrait couvre réellement tout le morceau.
   */
  previewCoverage?: number | null;
  /**
   * Pose les repères de lecture : les bandes de territoire des deux mains et
   * les règles horizontales.
   *
   * Réservé à la fiche, pour la même raison que `label` : sur une tuile de
   * 200 × 150 en grille, une règle tous les quatre rangs ajoute cinq traits par
   * carte et vingt-quatre cartes de bruit à la page, sans rien résoudre — le
   * vide y est trop petit pour ressembler à un bug. C'est en grand, quand le
   * cadre fait 460 px et que le morceau n'en remplit qu'un dixième, que
   * l'absence de repère devient un problème.
   *
   * Purement décoratif : rien de tout ça n'entre dans `label`.
   */
  showReferences?: boolean;
};

export function PieceSilhouette({
  silhouette,
  width,
  height,
  className,
  label,
  showReferences = false,
  progress = null,
  previewCoverage = null,
}: Props) {
  const decoded = decodeSilhouette(silhouette);
  const box: SilhouetteBox = { width, height, gap: CELL_GAP };

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      /*
        Le conteneur a les mêmes proportions que le repère (c'est à ça que
        servent `width` et `height` côté appelant), donc « none » ne déforme
        rien : il évite seulement le letterboxing quand l'arrondi d'un pixel
        fait diverger les deux d'un poil.
      */
      preserveAspectRatio="none"
      className={className}
      role={label === undefined ? "presentation" : "img"}
      aria-label={label}
      aria-hidden={label === undefined ? true : undefined}
    >
      <rect x={0} y={0} width={width} height={height} fill={SILHOUETTE_BACKGROUND} />

      {decoded !== null && showReferences && (
        <References silhouette={decoded} box={box} />
      )}

      {/*
        Rien à dessiner : score non analysé, ou silhouette d'une version du
        format qu'on ne sait pas lire. On laisse l'aplat nu — inventer une
        figure de remplacement rendrait les deux cas indiscernables d'un
        morceau réellement clairsemé.
      */}
      {decoded !== null && <SilhouetteMarks silhouette={decoded} box={box} />}

      {progress !== null && (
        <Playhead
          x={progress * (previewCoverage ?? 1) * width}
          boundaryX={
            previewCoverage !== null && previewCoverage < 1
              ? previewCoverage * width
              : null
          }
          height={height}
        />
      )}
    </svg>
  );
}

/**
 * Les marques d'une silhouette, sans cadre ni fond : un `<g>` par main, à
 * poser dans un `<svg>` que l'appelant possède.
 *
 * Exporté parce que la pochette d'une playlist empile plusieurs figures dans
 * **un seul** repère, chacune décalée et rognée sur sa tranche de rangs — voir
 * `playlist-cover.tsx`. Un `<svg>` imbriqué par morceau aurait fait le même
 * dessin, avec quatre repères de coordonnées à tenir au lieu d'un.
 *
 * `window` rogne ; sans lui, la figure entière occupe le cadre.
 */
export function SilhouetteMarks({
  silhouette,
  box,
  window = null,
}: {
  silhouette: Silhouette;
  box: SilhouetteBox;
  window?: SilhouetteRowWindow | null;
}) {
  const rects = silhouetteRects(silhouette, box, window);

  return (
    <>
      {PAINT_ORDER.map((hand) => (
        <g key={hand} fill={HAND_FILL[hand]}>
          {rects
            .filter((rect) => rect.hand === hand)
            .map((rect) => (
              <rect
                key={`${rect.row}-${rect.col}`}
                x={rect.x}
                y={rect.y}
                width={rect.width}
                height={rect.height}
                rx={CELL_RADIUS}
                fillOpacity={cellAlpha(rect.density)}
              />
            ))}
        </g>
      ))}
    </>
  );
}

/**
 * La tranche de rangs la plus dense pour un cadre de `bandHeight` de haut,
 * la maille gardant le pas qu'elle a sur une tuile de `width` de large.
 *
 * Ici et non dans `silhouette.ts` parce que c'est la traduction d'une hauteur
 * **en pixels** vers un nombre de rangs : elle dépend du pas de maille, donc
 * d'un réglage de dessin, pas du format.
 */
export function bandRowWindow(
  silhouette: Silhouette,
  width: number,
  bandHeight: number,
): SilhouetteRowWindow {
  const mesh = width / silhouette.cols;

  // Deux rangs au minimum : à un seul, la bande n'est plus une figure, c'est
  // une ligne de points, et quatre lignes de points empilées ne disent rien.
  return densestRowWindow(silhouette, Math.max(2, Math.round(bandHeight / mesh)));
}

/**
 * La tête de lecture de l'aperçu.
 *
 * Un halo large et translucide sous un trait fin : le trait seul, à 1,5 px sur
 * un fond presque noir, se perd dès qu'il traverse une zone vide — et sur une
 * pièce clairsemée, la moitié de sa course est vide. Le halo lui donne du
 * corps sans épaissir le repère.
 *
 * Le déjà-joué est assombri plutôt que le reste éclairci : la figure garde ses
 * couleurs de main, qui sont ce qu'on est venu lire.
 */
function Playhead({
  x,
  boundaryX,
  height,
}: {
  x: number;
  boundaryX: number | null;
  height: number;
}) {
  return (
    <g aria-hidden="true">
      <rect x={0} y={0} width={x} height={height} fill="#000" fillOpacity={0.38} />

      {/*
        Où l'aperçu s'arrêtera. Sans ce repère, une tête qui parcourt 4 % de la
        figure puis s'immobilise se lit comme un lecteur en panne, alors qu'elle
        dit la vérité : on n'a entendu que le début.
      */}
      {boundaryX !== null && (
        <rect
          x={boundaryX - 0.5}
          y={0}
          width={1}
          height={height}
          fill="var(--color-accent-bright)"
          fillOpacity={0.35}
        />
      )}
      <rect
        x={x - 2.5}
        y={0}
        width={5}
        height={height}
        fill="var(--color-accent-bright)"
        fillOpacity={0.28}
      />
      <rect x={x - 0.75} y={0} width={1.5} height={height} fill="#fff" fillOpacity={0.9} />
    </g>
  );
}

/**
 * Les bandes de territoire puis les règles, dans cet ordre : une règle qui
 * passerait sous une bande se verrait deux fois moins d'un côté de sa frontière
 * que de l'autre, et la bande se lirait comme un défaut d'alignement.
 */
function References({
  silhouette,
  box,
}: {
  silhouette: Silhouette;
  box: SilhouetteBox;
}) {
  const rules: number[] = [];

  for (let row = RULE_ROW_STEP; row < silhouette.rows; row += RULE_ROW_STEP) {
    rules.push(silhouetteRowOffset(silhouette, box, row));
  }

  return (
    <g aria-hidden="true">
      {PAINT_ORDER.map((hand) => {
        const territory = handTerritory(silhouette, box, hand);

        if (territory === null) {
          return null;
        }

        return (
          <rect
            key={hand}
            x={0}
            y={territory.y}
            width={box.width}
            height={territory.height}
            fill={HAND_TERRITORY_FILL[hand]}
          />
        );
      })}

      {rules.map((y) => (
        <rect
          key={y}
          x={0}
          y={y}
          width={box.width}
          height={RULE_THICKNESS}
          fill={RULE_COLOR}
        />
      ))}
    </g>
  );
}
